import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

const CONTEXT_CART_COUNT_SELECTOR = '#header-component [data-sc-context-cart-count]';
const SHOP_SELECTOR = '[data-sc-shop]';

/**
 * Updates only Sunday Clovr's contextual Bag / Selection count markers.
 * Horizon's cart bubble remains exclusively owned by cart-icon.js.
 * @param {number} itemCount
 */
const updateContextCartCount = (itemCount) => {
  if (!Number.isInteger(itemCount) || itemCount < 0) return;

  for (const marker of document.querySelectorAll(CONTEXT_CART_COUNT_SELECTOR)) {
    marker.textContent = String(itemCount);
  }
};

/**
 * Header actions component that manages cart notifications and the
 * cart-drawer trigger's `aria-expanded` state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  /** @type {AbortController | null} */
  #contextCartRefreshController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    window.addEventListener('pageshow', this.#onPageShow);
    this.#syncCartTriggerAriaExpanded();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    window.removeEventListener('pageshow', this.#onPageShow);
    this.#contextCartRefreshController?.abort();
    this.#contextCartRefreshController = null;
  }

  #syncCartTriggerAriaExpanded = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    const trigger = this.querySelector('[aria-controls="cart-drawer"]');
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', cartDrawer.hasAttribute('open') ? 'true' : 'false');
  };

  /**
   * Syncs `aria-expanded` on the cart-drawer trigger when the drawer opens or closes.
   * @param {Event} event
   */
  #onDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#syncCartTriggerAriaExpanded();
  };

  /**
   * Reconciles a contextual count when a product page is restored from bfcache,
   * or when a non-standard cart event lacks the promised result. Shop pages
   * keep using sc-shop.js, which already refreshes their bag and totals together.
   */
  #refreshContextCartCount = async () => {
    if (!document.querySelector(CONTEXT_CART_COUNT_SELECTOR) || document.querySelector(SHOP_SELECTOR)) {
      return;
    }

    this.#contextCartRefreshController?.abort();
    const controller = new AbortController();
    this.#contextCartRefreshController = controller;

    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Cart refresh failed with status ${response.status}`);

      const cart = await response.json();
      if (!controller.signal.aborted) updateContextCartCount(cart.item_count);
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[header-actions] Context cart refresh failed:', error);
    } finally {
      if (this.#contextCartRefreshController === controller) this.#contextCartRefreshController = null;
    }
  };

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (event.persisted) this.#refreshContextCartCount();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    if (!event.promise || typeof event.promise.then !== 'function') {
      this.#refreshContextCartCount();
      return;
    }

    event.promise
      .then(({ cart }) => {
        const cartCount = cart?.totalQuantity;
        if (!Number.isInteger(cartCount) || cartCount < 0) {
          this.#refreshContextCartCount();
          return;
        }

        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
        updateContextCartCount(cartCount);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[header-actions] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
